import { z } from "zod";

/**
 * Inviti a un gruppo.
 *
 * Il codice è di **8 caratteri** come nei mockup, da un alfabeto senza
 * omografi: niente `0`/`O`, niente `1`/`I`/`L`. Chi lo detta al telefono o lo
 * ricopia da uno schermo non deve poter sbagliare.
 */
export const INVITE_CODE_LENGTH = 8;
export const INVITE_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

const codePattern = new RegExp(`^[${INVITE_CODE_ALPHABET}]{${INVITE_CODE_LENGTH}}$`);

/** Normalizza ciò che l'utente digita: maiuscole, senza trattini né spazi. */
export function normalizeInviteCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s-]/g, "");
}

/**
 * Il codice, da qualunque cosa l'utente abbia incollato.
 *
 * Perché non basta `normalizeInviteCode`: chi riceve un invito riceve un
 * **link**, non un codice. Il gesto naturale davanti a un campo «codice» è
 * incollare quello che si ha in mano, e senza questa estrazione ci si trova
 * davanti a un pulsante spento senza capire perché — con il codice giusto
 * negli appunti.
 *
 * Riconosce l'indirizzo completo, il solo percorso, e il codice nudo:
 *
 *     https://qota.example/i/4F2K9XPM   →  4F2K9XPM
 *     /i/4F2K-9XPM                      →  4F2K9XPM
 *     4f2k 9xpm                         →  4F2K9XPM
 *
 * Non valida: dice solo *cosa* sembra il codice. A rifiutarlo, se non lo è, ci
 * pensa `inviteCodeSchema`.
 */
export function inviteCodeFromInput(raw: string): string {
  const trimmed = raw.trim();

  // L'ultimo segmento dopo `/i/`, ignorando eventuali code di query o ancore.
  const fromLink = /\/i\/([^/?#\s]+)/i.exec(trimmed);
  if (fromLink?.[1]) return normalizeInviteCode(fromLink[1]);

  return normalizeInviteCode(trimmed);
}

/** `4F2K9XPM` diventa `4F2K-9XPM`: più facile da leggere e da dettare. */
export function formatInviteCode(code: string): string {
  return code.length === INVITE_CODE_LENGTH ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

export const inviteCodeSchema = z
  .string()
  .transform(normalizeInviteCode)
  .refine((value) => codePattern.test(value), "Codice non valido");

export const createInviteRequestSchema = z.object({
  /**
   * Se valorizzato, l'invito è **per quella persona**: accettandolo prende il
   * posto del segnaposto e ne eredita le spese, invece di entrare da zero.
   */
  forMemberId: z.string().min(1).optional(),
  /** Durata in giorni. Oltre non ha senso: un invito eterno è una porta aperta. */
  expiresInDays: z.number().int().min(1).max(30).default(7),
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

export const inviteResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  groupId: z.string(),
  /** Nome del segnaposto a cui è destinato, se l'invito è mirato. */
  forDisplayName: z.string().nullable(),
  expiresAt: z.string(),
  usedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type InviteResponse = z.infer<typeof inviteResponseSchema>;

/**
 * L'anteprima che vede chi riceve l'invito, **prima** di avere un account.
 *
 * Volutamente povera: nome del gruppo, chi invita, quanto grande è. Nessun
 * elenco di membri, nessuna email, nessun importo per persona — chi ha in mano
 * un codice non è ancora nessuno.
 */
export const invitePreviewResponseSchema = z.object({
  groupName: z.string(),
  invitedBy: z.string(),
  memberCount: z.number().int().nonnegative(),
  expenseCount: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  currency: z.string(),
  /** Il nome del segnaposto che questa persona andrebbe a occupare. */
  forDisplayName: z.string().nullable(),
  /** Quante spese sono già registrate a suo nome: «non riparti da zero». */
  expensesAwaiting: z.number().int().nonnegative(),
  expiresAt: z.string(),
});
export type InvitePreviewResponse = z.infer<typeof invitePreviewResponseSchema>;

export const acceptInviteResponseSchema = z.object({
  groupId: z.string(),
  groupName: z.string(),
  /** `true` se ha preso il posto di un segnaposto, ereditandone lo storico. */
  promotedFromPlaceholder: z.boolean(),
  /** `true` se faceva già parte del gruppo: l'invito non ha cambiato nulla. */
  alreadyMember: z.boolean(),
});
export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>;
